import { ProviderUser } from "@/common/types/ProviderUser";
import AccountDetailsOnboardingStep from "../components/AccountDetailsOnboardingStep";
import CredentialUploadStep from "../components/CredentialUploadStep";
import JobPreferencesOnboardingStep from "../components/JobPreferencesOnboardingStep";
import PushNotificationsOnboarding from "../components/PushNotificationsOnboarding";
import { ProviderOnboardingStep } from "../enums/ProviderOnboardingStep";
import TextNotificationsOnboarding from "../components/TextNotificationsOnboarding";
import { Bell, BriefcaseBusiness, Upload, Shield } from "lucide-react";
import LicensesOnboardingStep from "../components/LicensesOnboardingStep";
import {
  type ActiveOnboardingStep,
  type OnboardingStepRule,
  getVisibleOnboardingStepRules,
} from "./onboarding-step-rules";

type OnboardingStepDisplay = {
  title: string;
  component: React.ComponentType<{
    onComplete: () => void;
    onBack?: () => void;
    providerUser: ProviderUser;
  }>;
  icon: React.ComponentType<{ className: string }>;
  hideStepper: boolean;
};

export type OnboardingStep = OnboardingStepRule & OnboardingStepDisplay;

// Step order and completion rules live in `onboarding-step-rules.ts`; see there
// for why they are kept apart from these components.
const ONBOARDING_STEP_DISPLAY: Record<
  ActiveOnboardingStep,
  OnboardingStepDisplay
> = {
  [ProviderOnboardingStep.ACCOUNT_DETAILS]: {
    title: "Account Details",
    component: AccountDetailsOnboardingStep,
    hideStepper: false,
    icon: ({ className }: { className: string }) => (
      <svg
        className={className}
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        fill="currentColor"
        viewBox="0 0 20 16"
      >
        <path d="M18 0H2a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2ZM6.5 3a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3.014 13.021l.157-.625A3.427 3.427 0 0 1 6.5 9.571a3.426 3.426 0 0 1 3.322 2.805l.159.622-6.967.023ZM16 12h-3a1 1 0 0 1 0-2h3a1 1 0 0 1 0 2Zm0-3h-3a1 1 0 1 1 0-2h3a1 1 0 1 1 0 2Zm0-3h-3a1 1 0 1 1 0-2h3a1 1 0 1 1 0 2Z"></path>
      </svg>
    ),
  },
  [ProviderOnboardingStep.LICENSES]: {
    title: "Medical Licenses",
    hideStepper: false,
    component: LicensesOnboardingStep,
    icon: ({ className }: { className: string }) => (
      <Shield className={className} />
    ),
  },
  [ProviderOnboardingStep.SHIFT_PREFERENCES]: {
    title: "Job Preferences",
    hideStepper: false,
    component: JobPreferencesOnboardingStep,
    icon: ({ className }: { className: string }) => (
      <BriefcaseBusiness className={className} />
    ),
  },
  [ProviderOnboardingStep.TEXT_NOTIFICATIONS]: {
    title: "Text Notifications",
    hideStepper: false,
    component: TextNotificationsOnboarding,
    icon: ({ className }: { className: string }) => (
      <Bell className={className} />
    ),
  },
  [ProviderOnboardingStep.PUSH_NOTIFICATIONS]: {
    title: "Push Notifications",
    hideStepper: false,
    component: PushNotificationsOnboarding,
    icon: ({ className }: { className: string }) => (
      <Bell className={className} />
    ),
  },
  [ProviderOnboardingStep.UPLOAD_DOCUMENTS]: {
    title: "Upload Documents",
    hideStepper: false,
    component: CredentialUploadStep,
    icon: ({ className }: { className: string }) => (
      <Upload className={className} />
    ),
  },
};

/**
 * Returns all onboarding steps visible to this user on the current platform.
 * Use this for navigation to allow users to go back to completed steps.
 */
export function getAllOnboardingSteps(
  user?: ProviderUser | null,
): OnboardingStep[] {
  return getVisibleOnboardingStepRules(user).map((rule) => ({
    ...rule,
    ...ONBOARDING_STEP_DISPLAY[rule.id],
  }));
}

/**
 * Returns only incomplete onboarding steps for the current platform.
 * Use this to determine the first incomplete step or check if onboarding is complete.
 */
export function getOnboardingSteps(user: ProviderUser): OnboardingStep[] {
  return getAllOnboardingSteps(user).filter((step) => !step.valid(user));
}
